import { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { API, api } from "@/lib/api";
import { fmtDate, fmtEuro, fmtNum } from "@/lib/format";
import { Download, Loader2, LogOut } from "lucide-react";

const STATUSES = ["nouveau", "contacté", "rdv planifié", "estimation affinée", "mandat signé"];

function formatApiError(detail) {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg).filter(Boolean).join(" ");
  return "Une erreur est survenue.";
}

function StatCard({ label, value, testid }) {
  return (
    <div className="border border-stone-800 bg-[#141417] p-5">
      <p data-testid={testid} className="font-display text-2xl text-[#E5C158]">{value}</p>
      <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">{label}</p>
    </div>
  );
}

export default function AdminDashboard() {
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user ?? undefined);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState(null);
  const [loggingIn, setLoggingIn] = useState(false);

  const loadLeads = useCallback(async () => {
    setLoadingLeads(true);
    try {
      const res = await api.get("/admin/leads");
      setLeads(res.data);
    } catch {
      toast.error("Impossible de charger les leads.");
    } finally {
      setLoadingLeads(false);
    }
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(null));
  }, []);

  useEffect(() => {
    if (user) loadLeads();
  }, [user, loadLeads]);

  const login = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    setLoginError(null);
    try {
      const res = await api.post("/auth/login", { email, password });
      setUser(res.data);
      toast.success("Connexion réussie.");
    } catch (err) {
      setLoginError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoggingIn(false);
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(null);
    setLeads([]);
  };

  const updateStatus = async (leadId, status) => {
    try {
      await api.patch(`/admin/leads/${leadId}`, { status });
      setLeads((ls) => ls.map((l) => (l.id === leadId ? { ...l, status } : l)));
      api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
      toast.success("Statut mis à jour.");
    } catch {
      toast.error("Échec de la mise à jour.");
    }
  };

  if (user === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0B0B0D]">
        <Loader2 data-testid="admin-loading" className="animate-spin text-[#D4AF37]" size={28} />
      </div>
    );
  }

  if (!user) {
    return (
      <div data-testid="admin-login-page" className="flex min-h-screen items-center justify-center bg-[#0B0B0D] px-6 text-[#F5F3EF]">
        <div className="w-full max-w-md border border-stone-800 bg-[#141417] p-10">
          <p className="font-display text-2xl">
            Valeur <span className="italic text-[#D4AF37]">Prestige</span>
          </p>
          <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.3em] text-stone-500">Espace conseiller</p>
          <form onSubmit={login} className="mt-8 space-y-5" data-testid="admin-login-form">
            <div>
              <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-stone-400">Email</label>
              <input
                data-testid="admin-login-email"
                type="email"
                className="input-dark"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-stone-400">Mot de passe</label>
              <input
                data-testid="admin-login-password"
                type="password"
                className="input-dark"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            {loginError && (
              <p data-testid="admin-login-error" className="text-xs text-red-400">{loginError}</p>
            )}
            <button type="submit" data-testid="admin-login-submit" disabled={loggingIn} className="btn-gold w-full justify-center disabled:opacity-60">
              {loggingIn ? <Loader2 size={16} className="animate-spin" /> : "Se connecter"}
            </button>
          </form>
          <div className="mt-6 flex items-center gap-4">
            <span className="h-px flex-1 bg-stone-800" />
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone-600">ou</span>
            <span className="h-px flex-1 bg-stone-800" />
          </div>
          <button
            type="button"
            data-testid="admin-login-google"
            onClick={googleLogin}
            className="btn-ghost mt-6 w-full justify-center !text-xs"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#EA4335" d="M12 5.04c1.62 0 3.06.56 4.2 1.64l3.12-3.12C17.46 1.8 14.96.75 12 .75 7.62.75 3.94 3.27 2.2 6.93l3.66 2.84C6.77 7.1 9.13 5.04 12 5.04z" />
              <path fill="#4285F4" d="M23.25 12.27c0-.79-.07-1.54-.2-2.27H12v4.51h6.32c-.27 1.48-1.09 2.72-2.32 3.55l3.55 2.76c2.08-1.92 3.7-4.76 3.7-8.55z" />
              <path fill="#FBBC05" d="M5.87 14.23a6.9 6.9 0 0 1 0-4.46L2.2 6.93a11.26 11.26 0 0 0 0 10.14l3.66-2.84z" />
              <path fill="#34A853" d="M12 23.25c3.04 0 5.6-1 7.46-2.72l-3.55-2.76c-.98.66-2.24 1.06-3.91 1.06-2.87 0-5.23-2.06-6.14-4.6l-3.66 2.84c1.74 3.66 5.42 6.18 9.8 6.18z" />
            </svg>
            Continuer avec Google
          </button>
          <Link to="/" data-testid="admin-login-home-link" className="mt-6 block text-center text-xs text-stone-500 transition-colors hover:text-stone-300">
            ← Retour au site
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#0B0B0D] text-[#F5F3EF]">
      <header className="border-b border-[#D4AF37]/10 bg-[#0B0B0D]/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <div className="flex items-baseline gap-3">
            <span className="font-display text-xl">Valeur <span className="italic text-[#D4AF37]">Prestige</span></span>
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-stone-500">Leads</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              data-testid="admin-export-csv"
              onClick={() => window.open(`${API}/admin/leads.csv`, "_blank")}
              className="btn-ghost !px-4 !py-2 !text-xs"
            >
              <Download size={14} /> Export CSV
            </button>
            <button type="button" data-testid="admin-logout-button" onClick={logout} className="btn-ghost !px-4 !py-2 !text-xs">
              <LogOut size={14} /> Déconnexion
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-10">
        <div className="flex items-end justify-between">
          <div>
            <h1 className="font-display text-3xl font-light text-amber-100">Demandes d'estimation</h1>
            <p data-testid="admin-leads-count" className="mt-2 text-sm text-stone-400">
              {leads.length} prospect{leads.length > 1 ? "s" : ""} — connecté : {user.email}
            </p>
          </div>
        </div>

        {stats && stats.total > 0 && (
          <div className="mt-10" data-testid="admin-stats">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
              <StatCard label="Prospects" value={stats.total} testid="stat-total" />
              <StatCard label="Ce mois-ci" value={stats.ce_mois} testid="stat-mois" />
              <StatCard label="Mandats signés" value={`${stats.signes} · ${stats.taux_signature}%`} testid="stat-signes" />
              <StatCard label="Données DVF réelles" value={`${stats.part_dvf}%`} testid="stat-dvf" />
              <StatCard label="Estimation médiane moyenne" value={fmtEuro(stats.estimation_moyenne)} testid="stat-median" />
            </div>
            {stats.top_rues.length > 0 && (
              <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
                <div className="border border-stone-800 p-6" data-testid="admin-top-rues">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone-500">Rues les plus demandées</p>
                  <div className="mt-4 space-y-3">
                    {stats.top_rues.map((r) => (
                      <div key={r.rue} data-testid={`top-rue-${r.rue.replace(/\s+/g, "-")}`} className="flex items-center gap-3">
                        <span className="w-48 truncate text-sm capitalize text-stone-300">{r.rue}</span>
                        <div className="h-1 flex-1 bg-stone-800">
                          <div className="h-1 bg-[#D4AF37]" style={{ width: `${(r.count / stats.top_rues[0].count) * 100}%` }} />
                        </div>
                        <span className="font-mono text-xs text-[#E5C158]">{r.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="border border-stone-800 p-6" data-testid="admin-top-secteurs">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone-500">Secteurs</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {stats.top_cp.map((c) => (
                      <span key={c.cp} data-testid={`top-cp-${c.cp}`} className="border border-[#D4AF37]/30 px-3 py-1.5 font-mono text-xs text-[#E5C158]">
                        {c.cp} · {c.count}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {loadingLeads ? (
          <div className="flex justify-center py-20">
            <Loader2 className="animate-spin text-[#D4AF37]" size={28} />
          </div>
        ) : leads.length === 0 ? (
          <p data-testid="admin-no-leads" className="mt-16 text-center text-sm text-stone-500">
            Aucune demande pour le moment. Partagez votre site pour recevoir vos premiers prospects.
          </p>
        ) : (
          <div className="mt-8 overflow-x-auto border border-stone-800">
            <table data-testid="admin-leads-table" className="w-full min-w-[1100px] text-left text-sm">
              <thead>
                <tr className="border-b border-stone-800 font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">
                  <th className="px-4 py-4">Date</th>
                  <th className="px-4 py-4">Contact</th>
                  <th className="px-4 py-4">Bien</th>
                  <th className="px-4 py-4">Estimation</th>
                  <th className="px-4 py-4">Projet</th>
                  <th className="px-4 py-4">Recontact</th>
                  <th className="px-4 py-4">Statut</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id} data-testid={`admin-lead-row-${l.id}`} className="border-b border-stone-800/60 align-top last:border-0 hover:bg-[#141417]">
                    <td className="px-4 py-4 text-stone-400">{fmtDate(l.created_at)}</td>
                    <td className="px-4 py-4">
                      <p className="text-stone-200">{l.lead.prenom} {l.lead.nom}</p>
                      <p className="mt-1 text-xs text-stone-500">{l.lead.email}</p>
                      <p className="text-xs text-stone-500">{l.lead.telephone}</p>
                      {l.rdv && (
                        <p data-testid={`lead-rdv-${l.id}`} className="mt-1.5 inline-flex border border-[#D4AF37]/40 px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest text-[#E5C158]">
                          RDV {fmtDate(l.rdv.date)} · {l.rdv.heure}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <p className="capitalize text-stone-200">{l.property.type_bien} · {fmtNum(l.property.surface)} m² · {l.property.pieces} p.</p>
                      <p className="mt-1 text-xs text-stone-500">{l.property.rue}, {l.property.code_postal} {l.property.ville}</p>
                      <p className="text-xs text-stone-500">DPE {l.property.dpe} · {l.property.etat}</p>
                      {l.photos?.length > 0 && (
                        <div className="mt-2 flex gap-1.5" data-testid={`lead-photos-${l.id}`}>
                          {l.photos.map((p, i) => (
                            <a key={p} href={`${API}/files/${p}`} target="_blank" rel="noreferrer" data-testid={`lead-photo-${l.id}-${i}`}>
                              <img
                                src={`${API}/files/${p}`}
                                alt={`Photo du bien ${i + 1}`}
                                className="h-10 w-10 border border-stone-700 object-cover transition-transform hover:scale-110"
                              />
                            </a>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <p className="text-[#E5C158]">{fmtEuro(l.result.bas)} — {fmtEuro(l.result.haut)}</p>
                      <p className="mt-1 text-xs text-stone-500">{fmtNum(l.result.prix_m2)} €/m²</p>
                    </td>
                    <td className="px-4 py-4 text-stone-300">{l.property.delai_projet}</td>
                    <td className="px-4 py-4">
                      <span className={`border px-2 py-1 font-mono text-[10px] uppercase tracking-widest ${l.lead.consent_recontact ? "border-emerald-500/40 text-emerald-300" : "border-red-500/40 text-red-300"}`}>
                        {l.lead.consent_recontact ? "Oui" : "Non"}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <select
                        data-testid={`admin-status-select-${l.id}`}
                        value={l.status || "nouveau"}
                        onChange={(e) => updateStatus(l.id, e.target.value)}
                        className="border border-stone-700 bg-[#141417] px-2 py-1.5 text-xs text-stone-200 outline-none focus:border-[#D4AF37]"
                      >
                        {STATUSES.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
